import { createTheme, alpha } from '@mui/material/styles';

export const createSunsetTheme = (mode = 'light') => {
  // Warm sunset colors
  const primaryColor = mode === 'light' ? '#e65100' : '#ff9e40';
  const secondaryColor = mode === 'light' ? '#b71c1c' : '#ff6659';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'light' ? '#ff833a' : '#ffb06b',
        dark: mode === 'light' ? '#ac1900' : '#c76a00',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor,
        light: mode === 'light' ? '#f05545' : '#ff9985',
        dark: mode === 'light' ? '#7f0000' : '#c50e29',
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'light' ? '#fffaf7' : '#2d1001',
        paper: mode === 'light' ? '#ffffff' : '#3e1c0a',
        subtle: mode === 'light' ? '#fff1e9' : '#4e2611',
      },
      text: {
        primary: mode === 'light' ? '#3e2723' : '#fff1e9',
        secondary: mode === 'light' ? '#6d4c41' : '#ffccbc',
      },
      error: {
        main: '#d50000',
      },
      warning: {
        main: '#ff6d00',
      },
      info: {
        main: '#0288d1',
      },
      success: {
        main: '#2e7d32',
      },
    },
    typography: {
      fontFamily: '"Raleway", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 500 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: mode === 'light' 
              ? '0 2px 8px rgba(230, 81, 0, 0.25)'
              : '0 2px 8px rgba(0, 0, 0, 0.3)',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0 4px 12px rgba(230, 81, 0, 0.4)'
                : '0 4px 12px rgba(0, 0, 0, 0.4)',
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: mode === 'light'
              ? 'linear-gradient(90deg, #e65100 0%, #b71c1c 100%)'
              : 'linear-gradient(90deg, #4e2611 0%, #3e1c0a 100%)',
            color: '#ffffff',
            boxShadow: mode === 'light'
              ? '0 2px 8px rgba(230, 81, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'light'
              ? '0 2px 12px rgba(0, 0, 0, 0.05)'
              : '0 2px 12px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: mode === 'light'
                ? '0 8px 24px rgba(0, 0, 0, 0.1)'
                : '0 8px 24px rgba(0, 0, 0, 0.3)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            '&.MuiChip-colorPrimary': {
              background: mode === 'light'
                ? `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.7)} 100%)`
                : primaryColor,
            },
            '&.MuiChip-colorSecondary': {
              background: mode === 'light'
                ? `linear-gradient(135deg, ${secondaryColor} 0%, ${alpha(secondaryColor, 0.7)} 100%)`
                : secondaryColor,
            },
          },
          outlined: {
            borderWidth: 1.5,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          elevation1: {
            boxShadow: mode === 'light'
              ? '0 2px 8px rgba(0, 0, 0, 0.05)'
              : '0 2px 8px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#fff1e9' : '#4e2611',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(230, 81, 0, 0.04)' : 'rgba(255, 158, 64, 0.08)',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            color: mode === 'light' ? '#3e2723' : '#fff1e9',
          },
        },
      },
    },
  });
};